import React, { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { IconButton, Slide, Tooltip } from '@mui/material';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import EditNoteRoundedIcon from '@mui/icons-material/EditNoteRounded';
import { CustomDelete } from 'components';
import { User, useModal } from 'utils';
import { useAiAssistant } from './aiAssistantContext';
import { usePanelWidth } from './usePanelWidth';
import { pageContext as buildPageContext } from './assistantPage';
import { AssistantMessage } from './assistantMessage';
import { AssistantComposer } from './assistantComposer';
import { AssistantCopy, NewChatCopy } from './aiAssistant.constants';
import { Body, BrandMark, Header, HeaderText, MessageList, Panel, ResizeHandle, Welcome, WelcomeList } from './aiAssistant.styles';

/** Distance from the bottom within which new content keeps the view pinned. */
const STICK_TO_BOTTOM_PX = 120;

const EmptyState = ({ firstName }) => (
    <Welcome>
        <BrandMark size={52} aria-hidden>
            <AutoAwesomeRoundedIcon />
        </BrandMark>
        <h3>{firstName ? `Hi ${firstName}, how can I help?` : 'How can I help?'}</h3>
        <p>{AssistantCopy.WELCOME_INTRO}</p>
        <WelcomeList>
            {AssistantCopy.WELCOME_POINTS.map((point) => (
                <li key={point}>{point}</li>
            ))}
        </WelcomeList>
        <p className="welcome-note">{AssistantCopy.WELCOME_NOTE}</p>
    </Welcome>
);

export const AiAssistantPanel = () => {
    const assistant = useAiAssistant();
    const location = useLocation();
    const { userInfo } = User();
    const { open, close: closeModal } = useModal();
    const bodyRef = useRef(null);
    const pinnedRef = useRef(true);
    const { width, isResizing, handleProps } = usePanelWidth();

    const { isEnabled, isOpen, close, chat } = assistant || {};
    const { messages = [], isBusy, send, reset, retry, quota, refreshQuota } = chat || {};

    const pageContext = useCallback(() => buildPageContext(location.pathname), [location.pathname]);

    const onSend = useCallback((text) => send(text, pageContext()), [send, pageContext]);
    const onRetry = useCallback((id) => retry(id, pageContext()), [retry, pageContext]);

    // A new chat discards this one on the server too — there is no way back to it, so ask first.
    const confirmNewChat = useCallback(
        () =>
            open(
                <CustomDelete
                    info={NewChatCopy.TITLE}
                    text={NewChatCopy.TEXT}
                    innerText={NewChatCopy.CONFIRM}
                    color="blue"
                    handleDel={() => {
                        reset();
                        closeModal();
                    }}
                />,
            ),
        [open, closeModal, reset],
    );

    const onScroll = () => {
        const el = bodyRef.current;
        pinnedRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < STICK_TO_BOTTOM_PX;
    };

    // What's left of today's limit can change outside this panel (other tabs, other AI features).
    useEffect(() => {
        if (isOpen) refreshQuota?.();
    }, [isOpen, refreshQuota]);

    // A new question always brings the view down. Declared first so it runs before the follow effect.
    useEffect(() => {
        pinnedRef.current = true;
    }, [messages.length]);

    // Follow the stream only while the user is at the bottom — scrolling up to re-read must not be yanked back.
    useEffect(() => {
        const el = bodyRef.current;
        if (el && pinnedRef.current) el.scrollTop = el.scrollHeight;
    }, [messages]);

    if (!isEnabled) return null;

    return (
        <Slide direction="left" in={isOpen} appear={false}>
            <Panel
                role="complementary"
                aria-label={AssistantCopy.TITLE}
                aria-hidden={!isOpen}
                style={{ width }}
                onKeyDown={(event) => event.key === 'Escape' && close()}
            >
                <ResizeHandle active={isResizing} {...handleProps} />
                <Header>
                    <BrandMark aria-hidden>
                        <AutoAwesomeRoundedIcon />
                    </BrandMark>
                    <HeaderText>
                        <h2>{AssistantCopy.TITLE}</h2>
                        <p>{AssistantCopy.SUBTITLE}</p>
                    </HeaderText>
                    <Tooltip title="New chat">
                        <span>
                            <IconButton onClick={confirmNewChat} disabled={!messages.length} aria-label="New chat">
                                <EditNoteRoundedIcon />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title="Close">
                        <IconButton onClick={close} aria-label="Close assistant">
                            <CloseRoundedIcon />
                        </IconButton>
                    </Tooltip>
                </Header>

                <Body ref={bodyRef} onScroll={onScroll} aria-live="polite">
                    {messages.length ? (
                        <MessageList>
                            {messages.map((message) => (
                                <AssistantMessage key={message.id} message={message} onRetry={onRetry} onNewChat={confirmNewChat} />
                            ))}
                        </MessageList>
                    ) : (
                        <EmptyState firstName={userInfo?.firstName} />
                    )}
                </Body>

                <AssistantComposer
                    isOpen={isOpen}
                    isBusy={isBusy}
                    onSend={onSend}
                    quota={quota}
                />
            </Panel>
        </Slide>
    );
};
